import { lazy, Suspense, useState } from "react"
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useParams } from "react-router-dom"
import {
  LayoutDashboard,
  Workflow,
  FileText,
  FilePlus2,
  ListTodo,
  Settings,
  PlayCircle,
  LogOut,
  Layers,
  Search,
  Link2,
  Sparkles,
  Wand2,
  Newspaper,
  ScrollText,
  BookOpen,
  Megaphone,
  Mail,
  Video,
  FileBarChart,
  Activity,
  Siren,
  User,
  Users,
  Circle,
  CheckCircle2,
  MoreVertical,
  Phone,
  Store,
  Receipt,
  Unlink,
  Waypoints,
  LayoutGrid,
  Rocket,
  Scale,
  GraduationCap,
  Loader2,
  Radar,
  MapPinned,
  Gauge,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { RouteErrorBoundary } from "@/components/route-error-boundary"
import { SidebarClientSelector } from "@/components/sidebar-client-selector"
import { HeaderUserProfile } from "@/components/header-user-profile"
import { ThemeToggle } from "@/components/theme-toggle"
import { DailyStoicDialog } from "@/components/daily-stoic-dialog"
import { DevToolsPopover } from "@/components/dev-tools-popover"
import { CollapsibleSidebarGroup } from "@/components/collapsible-sidebar-group"
import { SidebarNavBadge } from "@/components/sidebar-nav-badge"
import { BroadcastBanner } from "@/components/broadcast-banner"
import { Toaster } from "@/components/ui/toaster"
import { ArrivalOverlayHost } from "@/components/context-switch-overlay"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { apiClient, api } from "@/lib/api"

import { useQuery } from "@tanstack/react-query"
import { AuthProvider, useAuth } from "@/hooks/useAuth"
import { useStudioAccess, useStudioNav } from "@/hooks/use-studio"
import { WorkspaceProvider } from "@/components/workspace-switch"
import { useWorkspace, useWorkspaceAccess } from "@/hooks/use-workspace"
import { agencyItems, availableWorkspaces, redirectForPath, unknownPathFallback } from "@/lib/workspaces"
import { WorkspaceSwitcher } from "@/components/workspace-switcher"
import { AgencyNav, AgentsNav, EventsNav, OutreachNav, ProductsNav, StudioNav, SupportNav, TasksNav } from "@/components/sidebar/workspace-navs"
import { KeyboardShortcutProvider } from "@/hooks/use-keyboard-shortcuts"

// Stage badge component with tooltip
function StageBadge({ stage }: { stage: "alpha" | "beta" | "new" | "soon" }) {
  const config = {
    alpha: {
      label: "α",
      className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      tooltip: "Alpha — Experimental. Bugs expected and this feature may not work at all.",
    },
    beta: {
      label: "β",
      className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      tooltip: "Beta — Feature complete but still being refined. Minor bugs may exist.",
    },
    new: {
      label: "New",
      className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      tooltip: "New — Recently released feature, fully functional.",
    },
    soon: {
      label: "Soon",
      className: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
      tooltip: "Coming Soon — This feature is planned and under development.",
    },
  }

  const { label, className, tooltip } = config[stage]

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-medium cursor-help ${className}`}
            onClick={(e) => e.preventDefault()}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[200px]">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Legacy redirect component for /content/:id/edit -> /articles/:id/edit
function LegacyContentRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/articles/${id}/edit`} replace />
}

import { ForgeLoader } from "@/components/forge-loader"
import { ForgeMark } from "@/components/forge-mark"

// Pages
// Route components are loaded on demand. Named exports need the .then() wrapper
// because lazy() resolves a module whose default export is the component.
const LoginPage = lazy(() => import("@/pages/login").then((m) => ({ default: m.LoginPage })))
const NoAccessPage = lazy(() => import("@/pages/no-access").then((m) => ({ default: m.NoAccessPage })))
const DashboardPage = lazy(() => import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage })))
const BrokenLinksPage = lazy(() => import("@/pages/broken-links").then((m) => ({ default: m.BrokenLinksPage })))
const InternalLinksPage = lazy(() => import("@/pages/internal-links").then((m) => ({ default: m.InternalLinksPage })))
const WorkflowsPage = lazy(() => import("@/pages/workflows").then((m) => ({ default: m.WorkflowsPage })))
const WorkflowEditorPage = lazy(() => import("@/pages/workflow-editor").then((m) => ({ default: m.WorkflowEditorPage })))
const WorkflowRunDetailPage = lazy(() => import("@/pages/workflow-run-detail").then((m) => ({ default: m.WorkflowRunDetailPage })))
const TopicsLibraryPage = lazy(() => import("@/pages/topics-library").then((m) => ({ default: m.TopicsLibraryPage })))
const TopicBriefPage = lazy(() => import("@/pages/topic-brief").then((m) => ({ default: m.TopicBriefPage })))
const QueuePage = lazy(() => import("@/pages/queue").then((m) => ({ default: m.QueuePage })))
const LogsPage = lazy(() => import("@/pages/logs").then((m) => ({ default: m.LogsPage })))
const ContentPage = lazy(() => import("@/pages/content").then((m) => ({ default: m.ContentPage })))
const ContentEditorPage = lazy(() => import("@/pages/content-editor").then((m) => ({ default: m.ContentEditorPage })))
const ContentComparePage = lazy(() => import("@/pages/content-compare").then((m) => ({ default: m.ContentComparePage })))
const PromptsPage = lazy(() => import("@/pages/prompts").then((m) => ({ default: m.PromptsPage })))
const SettingsPage = lazy(() => import("@/pages/settings").then((m) => ({ default: m.SettingsPage })))
const ClientsTab = lazy(() => import("@/pages/settings/clients-tab").then((m) => ({ default: m.ClientsTab })))
const WebsiteConnectionsTab = lazy(() => import("@/pages/settings/website-connections-tab").then((m) => ({ default: m.WebsiteConnectionsTab })))
const ProfileTab = lazy(() => import("@/pages/settings/profile-tab").then((m) => ({ default: m.ProfileTab })))
const IntegrationsTab = lazy(() => import("@/pages/settings/integrations-tab").then((m) => ({ default: m.IntegrationsTab })))
const WebhooksTab = lazy(() => import("@/pages/settings/webhooks-tab").then((m) => ({ default: m.WebhooksTab })))
const ImageGenerationTab = lazy(() => import("@/pages/settings/image-generation-tab").then((m) => ({ default: m.ImageGenerationTab })))
const AnalyticsTab = lazy(() => import("@/pages/settings/analytics-tab").then((m) => ({ default: m.AnalyticsTab })))
const AccountTab = lazy(() => import("@/pages/settings/account-tab").then((m) => ({ default: m.AccountTab })))
const ApiTokensTab = lazy(() => import("@/pages/settings/api-tokens-tab").then((m) => ({ default: m.ApiTokensTab })))
const NewsroomTab = lazy(() => import("@/pages/settings/newsroom-tab").then((m) => ({ default: m.NewsroomTab })))
const TranscriptionsTab = lazy(() => import("@/pages/settings/transcriptions-tab").then((m) => ({ default: m.TranscriptionsTab })))
const SimilarityTab = lazy(() => import("@/pages/settings/similarity-tab").then((m) => ({ default: m.SimilarityTab })))
const NotificationsTab = lazy(() => import("@/pages/settings/notifications-tab").then((m) => ({ default: m.NotificationsTab })))
const SourceVerificationTab = lazy(() => import("@/pages/settings/source-verification-tab").then((m) => ({ default: m.SourceVerificationTab })))
const SiteProtocolTab = lazy(() => import("@/pages/settings/site-protocol-tab").then((m) => ({ default: m.SiteProtocolTab })))
const CompetitorsTab = lazy(() => import("@/pages/settings/competitors-tab").then((m) => ({ default: m.CompetitorsTab })))
const FirmProfileTab = lazy(() => import("@/pages/settings/firm-profile-tab").then((m) => ({ default: m.FirmProfileTab })))
const SharedPreviewPage = lazy(() => import("@/pages/shared-preview").then((m) => ({ default: m.SharedPreviewPage })))
// CMS is now embedded in /settings/websites
// keyword-research tools are now embedded in the /keywords page
const KeywordsPage = lazy(() => import("@/pages/keywords").then((m) => ({ default: m.KeywordsPage })))
const NewsroomPage = lazy(() => import("@/pages/newsroom").then((m) => ({ default: m.NewsroomPage })))
const TranscriptionsPage = lazy(() => import("@/pages/transcriptions").then((m) => ({ default: m.TranscriptionsPage })))
import { DocsProvider, useDocs } from "@/hooks/use-docs"
const CTAsPage = lazy(() => import("@/pages/ctas").then((m) => ({ default: m.CTAsPage })))
const ComponentsPage = lazy(() => import("@/pages/components").then((m) => ({ default: m.ComponentsPage })))
const ReportsPage = lazy(() => import("@/pages/reports").then((m) => ({ default: m.ReportsPage })))
const SiteActivityPage = lazy(() => import("@/pages/site-activity").then((m) => ({ default: m.SiteActivityPage })))
const AlertsPage = lazy(() => import("@/pages/alerts").then((m) => ({ default: m.AlertsPage })))
const AlertMonitorsPage = lazy(() => import("@/pages/alerts/monitors").then((m) => ({ default: m.AlertMonitorsPage })))
const CtaEditorPage = lazy(() => import("@/pages/cta-editor").then((m) => ({ default: m.CtaEditorPage })))
const CtaAnalyticsPage = lazy(() => import("@/pages/cta-analytics"))
const LeadsPage = lazy(() => import("@/pages/leads").then((m) => ({ default: m.LeadsPage })))
const SearchPage = lazy(() => import("@/pages/search").then((m) => ({ default: m.SearchPage })))
import { SearchModal } from "@/components/search-modal"
const AutopilotPage = lazy(() => import("@/pages/autopilot").then((m) => ({ default: m.AutopilotPage })))
const TasksPage = lazy(() => import("@/pages/tasks").then((m) => ({ default: m.TasksPage })))
const TasksSettingsPage = lazy(() => import("@/pages/tasks-settings").then((m) => ({ default: m.TasksSettingsPage })))
const ProjectsPage = lazy(() => import("@/pages/projects").then((m) => ({ default: m.ProjectsPage })))
const ProjectDetailPage = lazy(() => import("@/pages/project-detail").then((m) => ({ default: m.ProjectDetailPage })))
const AgentsSection = lazy(() => import("@/pages/agents").then((m) => ({ default: m.AgentsSection })))
const ProductsSection = lazy(() => import("@/pages/products").then((m) => ({ default: m.ProductsSection })))
const SupportSection = lazy(() => import("@/pages/support").then((m) => ({ default: m.SupportSection })))
const InvoicesPage = lazy(() => import("@/pages/invoices").then((m) => ({ default: m.InvoicesPage })))
const TrainingSection = lazy(() => import("@/pages/training").then((m) => ({ default: m.TrainingSection })))
const AdminLayout = lazy(() => import("@/pages/admin/admin-layout").then((m) => ({ default: m.AdminLayout })))
const AdminHqPage = lazy(() => import("@/pages/admin/admin-hq").then((m) => ({ default: m.AdminHqPage })))
const AdminErrorTrackingPage = lazy(() => import("@/pages/admin/admin-error-tracking").then((m) => ({ default: m.AdminErrorTrackingPage })))
const AdminSecretAccessPage = lazy(() => import("@/pages/admin/admin-secret-access").then((m) => ({ default: m.AdminSecretAccessPage })))
const AdminOverviewPage = lazy(() => import("@/pages/admin/admin-overview").then((m) => ({ default: m.AdminOverviewPage })))
const AdminClientHealthPage = lazy(() => import("@/pages/admin/admin-client-health").then((m) => ({ default: m.AdminClientHealthPage })))
const AdminClientsPage = lazy(() => import("@/pages/admin/admin-clients").then((m) => ({ default: m.AdminClientsPage })))
const AdminClientDetailPage = lazy(() => import("@/pages/admin/admin-client-detail").then((m) => ({ default: m.AdminClientDetailPage })))
const AdminTeamPage = lazy(() => import("@/pages/admin/admin-team").then((m) => ({ default: m.AdminTeamPage })))
const AdminUsersPage = lazy(() => import("@/pages/admin/admin-users").then((m) => ({ default: m.AdminUsersPage })))
const AdminDocsPage = lazy(() => import("@/pages/admin/admin-docs").then((m) => ({ default: m.AdminDocsPage })))
const AdminLicensesPage = lazy(() => import("@/pages/admin/admin-licenses").then((m) => ({ default: m.AdminLicensesPage })))
const AdminSiteDetailPage = lazy(() => import("@/pages/admin/admin-site-detail").then((m) => ({ default: m.AdminSiteDetailPage })))
const AdminFeedbackPage = lazy(() => import("@/pages/admin/admin-feedback").then((m) => ({ default: m.AdminFeedbackPage })))
const AdminPromptLogPage = lazy(() => import("@/pages/admin/admin-prompt-log").then((m) => ({ default: m.AdminPromptLogPage })))
const AdminAiVisibilityPage = lazy(() => import("@/pages/admin/admin-ai-visibility").then((m) => ({ default: m.AdminAiVisibilityPage })))
const AdminLocalRankPage = lazy(() => import("@/pages/admin/admin-local-rank").then((m) => ({ default: m.AdminLocalRankPage })))
const AdminClientPulsePage = lazy(() => import("@/pages/admin/admin-client-pulse").then((m) => ({ default: m.AdminClientPulsePage })))
const AdminClientDashboardsPage = lazy(() => import("@/pages/admin/admin-client-dashboards").then((m) => ({ default: m.AdminClientDashboardsPage })))
const AdminClientDashboardPreviewPage = lazy(() => import("@/pages/admin/admin-client-dashboard-preview").then((m) => ({ default: m.AdminClientDashboardPreviewPage })))
const ClientDashboardPage = lazy(() => import("@/pages/client-dashboard").then((m) => ({ default: m.ClientDashboardPage })))
const AdminApiActivityPage = lazy(() => import("@/pages/admin/admin-api-activity").then((m) => ({ default: m.AdminApiActivityPage })))
const AdminWebhooksPage = lazy(() => import("@/pages/admin/admin-webhooks").then((m) => ({ default: m.AdminWebhooksPage })))
const AdminPayrollPage = lazy(() => import("@/pages/admin/admin-payroll").then((m) => ({ default: m.AdminPayrollPage })))
const AdminPayrollPersonPage = lazy(() => import("@/pages/admin/admin-payroll-person").then((m) => ({ default: m.AdminPayrollPersonPage })))
const AdminCompensationPage = lazy(() => import("@/pages/admin/admin-compensation").then((m) => ({ default: m.AdminCompensationPage })))
const AdminCompensationPersonPage = lazy(() => import("@/pages/admin/admin-compensation").then((m) => ({ default: m.AdminCompensationPersonPage })))
const AdminPlatformSettingsPage = lazy(() => import("@/pages/admin/admin-platform-settings").then((m) => ({ default: m.AdminPlatformSettingsPage })))
const AdminSiteEmailPage = lazy(() => import("@/pages/admin/admin-site-email").then((m) => ({ default: m.AdminSiteEmailPage })))
const AdminUptimePage = lazy(() => import("@/pages/admin/admin-uptime").then((m) => ({ default: m.AdminUptimePage })))
const AdminUptimeDetailPage = lazy(() => import("@/pages/admin/admin-uptime-detail").then((m) => ({ default: m.AdminUptimeDetailPage })))
const SiteUptimePage = lazy(() => import("@/pages/site-uptime").then((m) => ({ default: m.SiteUptimePage })))
import { FeedbackModal } from "@/components/feedback-modal"
import { TasksHeaderLink } from "@/components/tasks-header-link"
import { useSiteUptime } from "@/lib/api/uptime"
import { STATUS_DOT, STATUS_LABEL, type MonitorStatus } from "@/lib/uptime"
import { cn } from "@/lib/utils"
import { legacyStatsTarget } from "@/lib/leads-page-params"
const CallTrackingPage = lazy(() => import("@/pages/call-tracking/index").then((m) => ({ default: m.CallTrackingPage })))
const GoogleBusinessPage = lazy(() => import("@/pages/google-business/index").then((m) => ({ default: m.GoogleBusinessPage })))
const BacklinksPage = lazy(() => import("@/pages/backlinks").then((m) => ({ default: m.BacklinksPage })))
const SearchConsolePage = lazy(() => import("@/pages/search-console/index").then((m) => ({ default: m.SearchConsolePage })))
const PracticeAreaRolloutRoute = lazy(() => import("@/pages/practice-area-rollout/guarded-route").then((m) => ({ default: m.PracticeAreaRolloutRoute })))
const SettlementEditorRoute = lazy(() => import("@/pages/settlements/guarded-route").then((m) => ({ default: m.SettlementEditorRoute })))
const SettlementNewRoute = lazy(() => import("@/pages/settlements/guarded-route").then((m) => ({ default: m.SettlementNewRoute })))
const SettlementsRoute = lazy(() => import("@/pages/settlements/guarded-route").then((m) => ({ default: m.SettlementsRoute })))
const StudioSection = lazy(() => import("@/pages/studio").then((m) => ({ default: m.StudioSection })))
const AiVisibilitySection = lazy(() => import("@/pages/ai-visibility").then((m) => ({ default: m.AiVisibilitySection })))
const LocalRankSection = lazy(() => import("@/pages/local-rank").then((m) => ({ default: m.LocalRankSection })))
const OutreachSection = lazy(() => import("@/pages/outreach").then((m) => ({ default: m.OutreachSection })))
const EventsSection = lazy(() => import("@/pages/events").then((m) => ({ default: m.EventsSection })))

/**
 * Sub-pages only render under their parent in the full sidebar, so the icon
 * rail would lose them. Its tooltip becomes a small menu instead — Radix keeps
 * a tooltip open while the pointer is over it, so the links are clickable.
 */
/** /leads/stats → /leads?tab=stats, keeping the rest of the query string. */
function LegacyLeadStatsRedirect() {
  const location = useLocation()
  return <Navigate to={legacyStatsTarget(location.search) + location.hash} replace />
}

function railFlyout(label: string, links: { to: string; label: string }[]) {
  return {
    className: "border bg-popover p-1.5 text-popover-foreground shadow-md",
    children: (
      <div className="min-w-36">
        <div className="px-2 pb-1 pt-0.5 text-xs font-medium">{label}</div>
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {link.label}
          </Link>
        ))}
      </div>
    ),
  }
}

function AppSidebar() {
  const location = useLocation()
  const { licenses } = useAuth()
  const { openDocs } = useDocs()

  // Whether user has any licenses (full feature access)
  const hasLicenses = licenses && licenses.length > 0
  const isTasksRoute = location.pathname === "/tasks" || location.pathname.startsWith("/tasks/")

  // Broken link count for the site (badge on the Broken Links nav item)
  const { data: brokenLinksStats } = useQuery({
    queryKey: ["broken-links", "stats"],
    queryFn: () => api.brokenLinks.stats(),
    staleTime: 60000,
    enabled: !!hasLicenses,
  })
  const brokenLinkCount = brokenLinksStats?.data?.broken_links || 0
  // Uptime for this site; empty (and the nav item hidden) when nothing is linked.
  const siteUptime = useSiteUptime().data?.monitors ?? []
  // A site's WordPress origin check arrives folded into its row; it counts the same.
  const siteChecks = siteUptime.flatMap((m) => (m.origin ? [m, m.origin] : [m]))
  const siteUptimeStatus: MonitorStatus = siteChecks.some((m) => m.status === "down")
    ? "down"
    : siteChecks.some((m) => m.status === "degraded")
      ? "degraded"
      : siteChecks.every((m) => m.status === "up")
        ? "up"
        : "unknown"

  // In-progress projects count (badge on the Projects nav item)
  const { data: inProgressProjects } = useQuery({
    queryKey: ["projects", { status: "in_progress" }],
    queryFn: () => api.projects.list({ status: "in_progress" }),
    staleTime: 60000,
    enabled: !!hasLicenses,
  })
  const inProgressProjectCount = inProgressProjects?.length || 0

  // Creator Studio: admins, or users licensed to a client with the
  // creator_studio integration. A Studio-only client's users see Studio and
  // the account basics, and nothing site-scoped.
  const { hideLicenseNav } = useStudioNav()
  const { current: workspace, available: availableWorkspaces, access: workspaceAccess } = useWorkspace()
  const showClientPicker = !!hasLicenses && !hideLicenseNav && workspace === "seo"

  // Check if CallRail is connected for conditional sidebar nav
  const { data: enabledIntegrations } = useQuery({
    queryKey: ["enabled-integrations"],
    queryFn: () => api.integrations.getEnabledIntegrations(),
    enabled: !!hasLicenses,
    staleTime: 60000,
  })
  const hasCallRail = enabledIntegrations?.integrations?.some(
    (i: any) => i.provider === "callrail" && i.connected
  )
  const hasGMB = enabledIntegrations?.integrations?.some(
    (i: any) => i.provider === "gmb"
  )
  // Practice Area Rollout is enabled per client via the `practice_rollout`
  // site_integration, so it stays invisible to everyone else.
  const hasPracticeRollout = enabledIntegrations?.integrations?.some(
    (i: any) => i.provider === "practice_rollout" && i.enabled
  )
  // Settlements is the Claim engagement's review desk, enabled per client via
  // the `claim_settlements` site_integration. Invisible to every other site.
  const hasClaimSettlements = enabledIntegrations?.integrations?.some(
    (i: { provider: string; enabled?: boolean }) =>
      i.provider === "claim_settlements" && i.enabled
  )
  // Total captured leads across the site (badge on the Leads nav item).
  // Only `total` is read, but the response still carries one real lead row —
  // contact details that must not reach localStorage, on every page for every
  // user. `select` would not help: it transforms on read, the raw row is cached.
  const { data: leadsCountData } = useQuery({
    queryKey: ["cta-leads-total"],
    queryFn: () => api.ctas.getAllLeads({ per_page: 1 }),
    enabled: !!hasLicenses,
    staleTime: 60000,
    meta: { persist: false },
  })
  const totalLeads = leadsCountData?.total || 0

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/"
    }
    return location.pathname.startsWith(path)
  }

  // Parse task view filter from URL for sidebar highlighting
  const searchParams = new URLSearchParams(location.search)
  const currentTaskView = isTasksRoute ? (searchParams.get("view") || "my") : ""

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-2 border-b border-border py-2.5">
        <WorkspaceSwitcher />
        {/* Divider between "which workspace" and "which client", so the two
            pickers don't read as one control. */}
        {showClientPicker && availableWorkspaces.length > 1 && (
          <div role="separator" className="mx-1 h-px bg-border group-data-[collapsible=icon]:hidden" />
        )}
        {/* The client picker belongs to SEO only (Studio is keyed by profile); the
            others span every client. With no switcher, the Forge mark holds
            the header as before. */}
        {showClientPicker ? (
          <SidebarClientSelector />
        ) : availableWorkspaces.length > 1 ? null : (
          <div className="px-1 py-1">
            <div className="flex items-center gap-3 px-1 py-1">
              <ForgeMark size={32} className="rounded-md" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">Forge</div>
              </div>
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="pt-1">
        {workspace === "studio" && <StudioNav />}
        {workspace === "outreach" && <OutreachNav />}
        {workspace === "events" && <EventsNav />}
        {workspace === "tasks" && <TasksNav hasLicenses={!!hasLicenses} inProgressProjectCount={inProgressProjectCount} />}
        {workspace === "agency" && <AgencyNav access={workspaceAccess} />}
        {workspace === "agents" && <AgentsNav />}
        {workspace === "products" && <ProductsNav />}
        {workspace === "support" && <SupportNav />}

        {/* Per-client features at the top of SEO, tinted so the client they
            exist for finds them first. Admin, Studio and Outreach moved into
            their own workspaces. */}
        {workspace === "seo" && !hideLicenseNav && hasLicenses && (hasPracticeRollout || hasClaimSettlements) && (
          <SidebarGroup className="py-1">
            <SidebarGroupContent>
              <SidebarMenu>
                {hasLicenses && hasPracticeRollout && (
                  <SidebarMenuItem>
                    {/* data-[active=true] is set explicitly because the base
                        sidebarMenuButtonVariants carry data-[active=true]:bg-muted,
                        which would otherwise strip the tint on the page itself. */}
                    <SidebarMenuButton asChild isActive={isActive("/practice-area-rollout")} tooltip="Practice Area Rollout" className="data-[active=true]:[&>svg]:text-current bg-[#5746af1a] text-[#5746af] hover:bg-[#5746af26] data-[active=true]:bg-[#5746af26] data-[active=true]:text-[#5746af] font-medium dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/20 dark:data-[active=true]:bg-violet-500/20 dark:data-[active=true]:text-violet-300">
                      <Link to="/practice-area-rollout">
                        <Rocket data-anim="launch" />
                        <span>Practice Area Rollout</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                {/* Settlements — Claim's review desk, and for that client the
                    main thing Forge is for, so it gets the same top slot the
                    rollout board has for Porter. */}
                {hasLicenses && hasClaimSettlements && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/settlements")} tooltip="Settlements" className="data-[active=true]:[&>svg]:text-current bg-[#0d74ce1a] text-[#0d74ce] hover:bg-[#0d74ce26] data-[active=true]:bg-[#0d74ce26] data-[active=true]:text-[#0d74ce] font-medium dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/20 dark:data-[active=true]:bg-sky-500/20 dark:data-[active=true]:text-sky-300">
                      <Link to="/settlements">
                        <Scale data-anim="tip" />
                        <span>Settlements</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {workspace === "seo" && hasLicenses && !hideLicenseNav && (<>
        {/* Dashboard with sub-pages */}
        <CollapsibleSidebarGroup title="Dashboard">
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/")} tooltip={railFlyout("Overview", [
              { to: "/queue", label: "Queue" },
              { to: "/logs", label: "Logs" },
              { to: "/autopilot", label: "Autopilot" },
            ])}>
              <Link to="/">
                <LayoutDashboard data-anim="tiles" />
                <span>Overview</span>
              </Link>
            </SidebarMenuButton>
            {(isActive("/") || isActive("/queue") || isActive("/logs") || isActive("/autopilot")) && (
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild isActive={isActive("/queue")}>
                    <Link to="/queue">Queue</Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild isActive={isActive("/logs")}>
                    <Link to="/logs">Logs</Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild isActive={isActive("/autopilot")}>
                    <Link to="/autopilot">Autopilot</Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        </CollapsibleSidebarGroup>

        {/* Core Sections — standalone items, not in a collapsible group */}
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/topics")} tooltip="Topics">
                  <Link to="/topics">
                    <ListTodo data-anim="checklist" />
                    <span>Topics</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>


              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/articles")} tooltip={railFlyout("Articles", [
                  { to: "/prompts", label: "Prompts" },
                  { to: "/workflows", label: "Workflows" },
                ])}>
                  <Link to="/articles">
                    <FileText data-anim="lines-doc" />
                    <span>Articles</span>
                  </Link>
                </SidebarMenuButton>
                {(isActive("/articles") || isActive("/pages") || isActive("/prompts") || isActive("/workflows")) && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive("/prompts")}>
                        <Link to="/prompts">Prompts</Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild isActive={isActive("/workflows")}>
                        <Link to="/workflows">Workflows</Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>

              {/* FOR-143 / Phase 3.5a — Pages sidebar item, sibling to Articles.
                  Same ContentPage component, locked to content_type='page'. */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/pages")} tooltip="Pages">
                  <Link to="/pages">
                    <FilePlus2 data-anim="plus" />
                    <span>Pages</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/backlinks")} tooltip="Backlinks">
                  <Link to="/backlinks">
                    <Link2 data-anim="link" />
                    <span>Backlinks</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/links")} tooltip="Broken Links">
                  <Link to="/links">
                    <Unlink data-anim="unlink" />
                    <span>Broken Links</span>
                    <SidebarNavBadge count={brokenLinkCount} tone="destructive" />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/internal-links")} tooltip="Internal Links">
                  <Link to="/internal-links">
                    <Waypoints data-anim="nodes" />
                    <span>Internal Links</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Tools (replaces Growth & Conversion) */}
        <CollapsibleSidebarGroup title="Tools">
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/keywords")} tooltip="Keywords">
              <Link to="/keywords">
                <Search data-anim="search" />
                <span>Keywords</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* AI Visibility — whether ChatGPT and Google's AI Overviews name
              this site for buyer prompts. Site-scoped, next to Keywords. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/ai-visibility")} tooltip="AI Visibility">
              <Link to="/ai-visibility">
                <Radar data-anim="spin" />
                <span>AI Visibility</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Local Rank — the site's Google Maps rank across a grid of points
              around each location (FOR-347). Site-scoped, next to AI Visibility. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/local-rank")} tooltip="Local Rank">
              <Link to="/local-rank">
                <MapPinned />
                <span>Local Rank</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/ctas")} tooltip="CTAs">
              <Link to="/ctas">
                <Megaphone data-anim="shout" />
                <span>CTAs</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Components — reusable content blocks embedded via
              [forge_component slug="..."], sibling to CTAs which ship the
              same way. Read-only library; authoring is CLI-side. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/components")} tooltip="Components">
              <Link to="/components">
                <LayoutGrid data-anim="tiles" />
                <span>Components</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            {/* Inbox and Stats are tabs of the one /leads page. */}
            <SidebarMenuButton asChild isActive={isActive("/leads")} tooltip="Leads">
              <Link to="/leads">
                <Mail data-anim="mail" />
                <span>Leads</span>
                <SidebarNavBadge count={totalLeads} tone="soft" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/reports")} tooltip="Reports">
              <Link to="/reports">
                <FileBarChart data-anim="chart" />
                <span>Reports</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Newsroom — the review lane for machine-drafted news. Sits with
              Reports and Site Activity because it is a place you go to check
              on the site, not a place you go to make something. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/newsroom")} tooltip="Newsroom">
              <Link to="/newsroom">
                <Newspaper data-anim="lines-news" />
                <span>Newsroom</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Alerts — what the monitors noticed. Sits beside Site Activity
              because both answer "is anything wrong here", one by watching
              and one by recording. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/alerts")} tooltip="Alerts">
              <Link to="/alerts">
                <Siren data-anim="siren" />
                <span>Alerts</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Uptime (FOR-303) — only for a site an admin linked a monitor to.
              The dot is the site's current state; the page is read-only. */}
          {siteUptime.length > 0 && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive("/uptime")} tooltip="Uptime">
                <Link to="/uptime">
                  <Gauge />
                  <span>Uptime</span>
                  <span className={cn("ml-auto h-2 w-2 shrink-0 rounded-full", STATUS_DOT[siteUptimeStatus])} title={STATUS_LABEL[siteUptimeStatus]} />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* Site Activity — the connector's audit trail for this site, next
              to Reports because both answer "what happened this week". */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/site-activity")} tooltip="Site Activity">
              <Link to="/site-activity">
                <Activity data-anim="trace" />
                <span>Site Activity</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/transcriptions")} tooltip="Transcriptions">
              <Link to="/transcriptions">
                <Video data-anim="record" />
                <span>Transcriptions</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {hasCallRail && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/call-tracking")} tooltip="Call Tracking">
              <Link to="/call-tracking">
                <Phone data-anim="ring" />
                <span>Call Tracking</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          )}
          {hasGMB && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/google-business")} tooltip="Google Business">
              <Link to="/google-business">
                <Store data-anim="awning" />
                <span>Google Business</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          )}
        </CollapsibleSidebarGroup>
        </>)}

        {/* Tasks moved to top nav bar */}
      </SidebarContent>

      {/* Docs and Settings are the SEO workspace's (client and site settings
          under /settings). Elsewhere each workspace's settings sit in its own
          nav, and personal settings in the avatar menu. */}
      {workspace === "seo" && (
      <SidebarFooter className="border-t border-border/40">
        <SidebarMenu>
          {!hideLicenseNav && (
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="Docs" onClick={() => openDocs()}>
                <BookOpen data-anim="book" />
                <span>Docs</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location.pathname.startsWith("/settings")} tooltip="Settings">
              <Link to="/settings">
                <Settings data-anim="spin" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      )}
      <SidebarRail />
    </Sidebar>
  )
}

/**
 * Shown while a route's chunk is still downloading.
 *
 * Deliberately not ForgeLoader: that fills the screen with the brand animation,
 * which is right for "we do not know who you are yet" and wrong for a
 * navigation between two pages of an app already on screen.
 */
function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  )
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, licenses } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <ForgeLoader />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Users with no licenses can still use Tasks — redirect them there
  // instead of no-access. Allow /tasks and /no-access pages.
  if (licenses.length === 0 && location.pathname !== "/no-access" && !location.pathname.startsWith("/tasks") && !location.pathname.startsWith("/invoices") && !location.pathname.startsWith("/training") && !location.pathname.startsWith("/prompt-log") && !location.pathname.startsWith("/studio") && !location.pathname.startsWith("/outreach") && !location.pathname.startsWith("/events") && !location.pathname.startsWith("/agents") && !location.pathname.startsWith("/products") && !location.pathname.startsWith("/support")) {
    return <Navigate to="/tasks" replace />
  }

  return <>{children}</>
}

// Top nav bar — hides search and docs for users without licenses
/**
 * The desktop sidebar's shape is a per-person setting (Settings → Account), so
 * it follows them between machines. The rail, the header toggle and Cmd+B all
 * write the same preference, rather than a cookie nobody reads back. The phone
 * drawer is separate state in SidebarProvider and always shows the full menu.
 */
function PreferredSidebarProvider({ children }: { children: React.ReactNode }) {
  const { user, updateUiPreferences } = useAuth()
  const open = user?.ui_preferences?.sidebar_style !== "icons"
  return (
    <SidebarProvider
      open={open}
      onOpenChange={(next) => {
        updateUiPreferences({ sidebar_style: next ? "full" : "icons" }).catch(() => {
          // updateUiPreferences already restored the previous style.
        })
      }}
    >
      {children}
    </SidebarProvider>
  )
}

/**
 * A Studio-only client's users get Creator Studio and their account settings;
 * every site-scoped page sends them back to Studio. The API decides who is
 * Studio-only (/studio/access → studio_only); this only follows it.
 *
 * Someone with custom workspace access (Admin → Users) is likewise sent from
 * a workspace they do not have to one they do. The API refuses those routes
 * anyway; this keeps them off a page that could only fail.
 */
/** No route matched: the home of the workspace the address belongs to, not always SEO. */
function UnknownRoute() {
  const { pathname } = useLocation()
  const access = useWorkspaceAccess()
  return <Navigate to={unknownPathFallback(pathname, access)} replace />
}

/**
 * Where an external person (outside the agency, Admin → Users → External) may
 * go: their boards' tasks and their own account. The API refuses everything
 * else (RestrictExternalUsers); this keeps them off pages that could only fail.
 */
function externalAllowsPath(path: string): boolean {
  return path === "/tasks" || path.startsWith("/tasks/")
    || path === "/settings" || path.startsWith("/settings/account") || path.startsWith("/settings/profile")
    || path.startsWith("/settings/api-tokens") || path.startsWith("/settings/llms")
    || path === "/no-access"
}

function StudioOnlyGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { user } = useAuth()
  const { data: studioAccess } = useStudioAccess()
  const access = useWorkspaceAccess()
  if (user?.is_external) {
    if (!externalAllowsPath(location.pathname)) return <Navigate to="/tasks" replace />
    return <>{children}</>
  }
  if (access.allowed && !access.studioPending) {
    const to = redirectForPath(location.pathname, availableWorkspaces(access), access)
    if (to && to !== location.pathname) return <Navigate to={to} replace />
  }
  if (studioAccess?.studio_only) {
    const path = location.pathname
    const allowed = path.startsWith("/studio") || path === "/settings" || path.startsWith("/settings/profile") || path.startsWith("/settings/account") || path.startsWith("/settings/api-tokens") || path === "/no-access"
    if (!allowed) return <Navigate to="/studio" replace />
  }
  return <>{children}</>
}

function TopNavBar() {
  const { licenses: allLicenses, currentLicense, user } = useAuth()
  const external = !!user?.is_external
  // Studio-only users get no site search, tasks, invoices or training: their
  // whole Forge is Creator Studio (hidden too while that is still unknown).
  const { hideLicenseNav: studioOnly } = useStudioNav()
  // Custom workspace access (Admin → Users) drops what this person lacks.
  const workspaceAccess = useWorkspaceAccess()
  const workspaces = availableWorkspaces(workspaceAccess)
  const showTasks = !studioOnly && workspaces.includes("tasks")
  const showAgencyBasics = !studioOnly && agencyItems(workspaceAccess).invoices
  const licenses = studioOnly || !workspaces.includes("seo") ? [] : allLicenses
  const hasLicenses = licenses && licenses.length > 0

  // Fetch autopilot status for the current site to show indicator
  const { data: autopilotStatus } = useQuery({
    queryKey: ["autopilot-status-topbar", currentLicense?.site_id],
    queryFn: () => api.autopilot.getStatus(),
    enabled: !!currentLicense?.site_id,
    refetchInterval: 60000, // Refresh every minute
    retry: false,
  })

  const isAutopilotActive = autopilotStatus?.enabled && autopilotStatus?.status === "active"
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <div className="sticky top-0 z-10 flex h-14 items-center gap-1 md:gap-4 border-b bg-background pl-1.5 pr-2 md:px-4">
      {/* Opens the drawer on a phone, switches labels/icons on desktop. A full
          40px target on touch: the 28px one sat flush against the search box
          and was easy to miss. */}
      <SidebarTrigger className="h-10 w-10 shrink-0 md:h-8 md:w-8 [&_svg]:size-5 md:[&_svg]:size-4" />

      {hasLicenses && <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />}

      {/* Autopilot active indicator */}
      {isAutopilotActive && (
        <Link to="/autopilot" className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors">
          <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
          Autopilot
        </Link>
      )}

      <div className="flex-1" />

      {/* Forge Agent chat — prototype (not wired up yet) */}

      {/* One evenly-spaced cluster for the utility controls: every trigger is
          an h-8 hit target with an h-4 muted icon, gap-1 throughout, so the
          header reads as a single icon set. Tasks leads, per Justin. */}
      <div className="flex items-center gap-1">
        {/* Search collapses to an icon on a phone, at the right with the
            other utilities, so the header has room for the menu button. */}
        {hasLicenses && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground md:hidden"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="h-4 w-4" />
            <span className="sr-only">Search</span>
          </Button>
        )}

        {/* Tasks — straight to /tasks, with the open-task count */}
        {showTasks && <TasksHeaderLink />}

        {/* Invoices — quick access in top nav */}
        {showAgencyBasics && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          asChild
        >
          <Link to="/invoices">
            <Receipt className="h-4 w-4" />
            <span className="sr-only">Invoices</span>
          </Link>
        </Button>
        )}

        {/* ---- Desktop: consolidated items ---- */}
        <div className="hidden md:flex items-center gap-1">
          <DevToolsPopover />
          <ThemeToggle />
          {/* Feedback files on our own FOR board: not for people outside the agency. */}
          {!external && <FeedbackModal />}
          <DailyStoicDialog />
        </div>
      </div>

      {/* ---- Mobile: overflow menu with all secondary items ---- */}
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden h-8 w-8">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">More options</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-72 px-4 pt-8">
          <SheetTitle className="text-sm font-semibold mb-4">Tools & Settings</SheetTitle>
          <nav className="flex flex-col gap-0.5">
            <div className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground">
              <DevToolsPopover />
              <span>Dev Tools</span>
            </div>

            <div className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground">
              <ThemeToggle />
              <span>Theme</span>
            </div>

            {!external && (
              <div className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground">
                <FeedbackModal />
                <span>Feedback</span>
              </div>
            )}

            {showAgencyBasics && <Link
              to="/training"
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-r from-brand-700 to-brand-600">
                <GraduationCap className="h-4 w-4 text-white" />
              </span>
              <span>Training</span>
            </Link>}

            <div className="my-1 border-t" />

            <div className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground">
              <DailyStoicDialog />
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Training — team guide, all logged-in users. A filled glacier gradient so it stands out (white on brand-600/700 is 7.2–9.3:1). */}
      {showAgencyBasics && <Link
        to="/training"
        title="Training"
        className="hidden sm:inline-flex h-8 items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-700 to-brand-600 px-3 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
      >
        <GraduationCap className="h-4 w-4" />
        Training
      </Link>}

      {/* User profile — always visible */}
      <HeaderUserProfile />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <KeyboardShortcutProvider>
      <BrowserRouter>
        {/* Completes the cross-client switch animation after a context-switch reload */}
        <ArrivalOverlayHost />
        {/* Covers the routes outside the shell — login and the public share
            links — which have no sidebar to keep on screen. */}
        <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/no-access" element={
            <PrivateRoute>
              <NoAccessPage />
            </PrivateRoute>
          } />
          <Route path="/preview/:token" element={<SharedPreviewPage />} />
          <Route path="/r/:token" element={<ClientDashboardPage />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <WorkspaceProvider>
              <PreferredSidebarProvider>
              <DocsProvider>
                <div className="flex h-screen w-full">
                  <AppSidebar />
                  <main className="flex-1 flex flex-col overflow-hidden">
                    <BroadcastBanner />
                    {/* relative: the containing block for anything absolutely
                        positioned inside a page (sr-only text, react-dropzone's
                        hidden file input). Without it they resolved against the
                        viewport, stretched the document past the shell, and the
                        window itself scrolled once this pane reached its end,
                        sliding the whole content area off-screen (task 3408). */}
                    <div className="relative flex-1 overflow-y-auto">
                    <TopNavBar />
                    {/* Inside the shell, so a route's chunk arriving late swaps
                        the content area and leaves the sidebar and header up —
                        and a route that fails to load shows its message there
                        too, instead of unmounting the whole root. */}
                    <RouteErrorBoundary>
                    <Suspense fallback={<RouteFallback />}>
                    <StudioOnlyGate>
                    <Routes>
                      <Route path="/" element={<div className="container py-6"><DashboardPage /></div>} />
                      <Route path="/autopilot" element={<div className="container py-6"><AutopilotPage /></div>} />
                      <Route path="/links" element={<div className="container py-6"><BrokenLinksPage /></div>} />
                      <Route path="/internal-links" element={<div className="container py-6"><InternalLinksPage /></div>} />
                      {/* FOR-143 / Phase 3.5a — Articles route excludes page-typed content
                          so the new /pages tab below has its own clean home. */}
                      <Route path="/articles" element={<div className="container py-6"><ContentPage excludeType="page" pageTitle="Articles" pageDescription="Blog posts, news, and other content" /></div>} />
                      <Route path="/articles/compare/:groupId" element={<div className="container py-6"><ContentComparePage /></div>} />
                      <Route path="/articles/:id/edit" element={<ContentEditorPage />} />
                      {/* /pages — same ContentPage component, locked to content_type='page'. */}
                      <Route path="/pages" element={<div className="container py-6"><ContentPage lockedType="page" pageTitle="Pages" pageDescription="Practice-area landing pages and other site pages" /></div>} />
                      <Route path="/pages/:id/edit" element={<ContentEditorPage />} />
                      <Route path="/content-updates" element={<Navigate to="/articles" replace />} />
                      {/* The publishing schedule is a section of Autopilot now. */}
                      <Route path="/publishing-schedule" element={<Navigate to="/autopilot#schedule" replace />} />
                      {/* Legacy redirects */}
                      <Route path="/content" element={<Navigate to="/articles" replace />} />
                      <Route path="/content/:id/edit" element={<LegacyContentRedirect />} />
                      <Route path="/topics" element={<div className="container py-6"><TopicsLibraryPage /></div>} />
                      <Route path="/topics/:id/brief" element={<div className="container py-6"><TopicBriefPage /></div>} />
                      {/* The topic generator is gone; old bookmarks land in the library. */}
                      <Route path="/topics/generate" element={<Navigate to="/topics" replace />} />
                      <Route path="/cms" element={<Navigate to="/settings/websites" replace />} />
                      <Route path="/backlinks" element={<div className="container py-6"><BacklinksPage /></div>} />
                      <Route path="/transcriptions" element={<div className="container py-6"><TranscriptionsPage /></div>} />
                      <Route path="/calendar" element={<Navigate to="/autopilot" replace />} />
                      <Route path="/ctas" element={<div className="container py-6"><CTAsPage /></div>} />
                      <Route path="/ctas/new" element={<CtaEditorPage />} />
                      <Route path="/ctas/:id/edit" element={<CtaEditorPage />} />
                      <Route path="/ctas/:id/analytics" element={<div className="container py-6"><CtaAnalyticsPage /></div>} />
                      <Route path="/components" element={<div className="container py-6"><ComponentsPage /></div>} />
                      <Route path="/leads" element={<div className="container py-6"><LeadsPage /></div>} />
                      {/* Lead Stats became the Stats tab of /leads (2026-10-22). */}
                      <Route path="/leads/stats" element={<LegacyLeadStatsRedirect />} />
                      <Route path="/reports" element={<div className="container py-6"><ReportsPage /></div>} />
                      <Route path="/newsroom" element={<div className="container py-6"><NewsroomPage /></div>} />
                      <Route path="/alerts" element={<div className="container py-6"><AlertsPage /></div>} />
                      <Route path="/uptime" element={<div className="container py-6"><SiteUptimePage /></div>} />
                      <Route path="/alerts/monitors" element={<div className="container py-6"><AlertMonitorsPage /></div>} />
                      <Route path="/site-activity" element={<div className="container py-6"><SiteActivityPage /></div>} />
                      {/* The team prompt log outside the admin area, for the
                          per-user Prompt log grant. Same page as
                          /admin/prompt-log; the API's own gate answers 403 for
                          anyone reaching the URL without the grant. */}
                      <Route path="/prompt-log" element={<div className="container py-6"><AdminPromptLogPage /></div>} />
                      <Route path="/call-tracking" element={<div className="container py-6"><CallTrackingPage /></div>} />
                      {/* Gated inside the element, not just hidden in the nav:
                          the URL stays reachable by hand and the API 404s for
                          clients without the practice_rollout integration. */}
                      <Route path="/practice-area-rollout" element={<PracticeAreaRolloutRoute />} />
                      {/* Same gating reasoning as practice-area-rollout: the
                          URL stays reachable by hand and the API 404s for
                          clients without the claim_settlements integration. */}
                      <Route path="/settlements" element={<SettlementsRoute />} />
                      <Route path="/settlements/new" element={<SettlementNewRoute />} />
                      <Route path="/settlements/:id" element={<SettlementEditorRoute />} />
                      <Route path="/google-business" element={<div className="container py-6"><GoogleBusinessPage /></div>} />
                      <Route path="/search-console" element={<Navigate to="/settings/search-console" replace />} />
                      <Route path="/keywords" element={<div className="container py-6"><KeywordsPage /></div>} />
                      <Route path="/ai-visibility/*" element={<div className="container py-6"><AiVisibilitySection /></div>} />
                      <Route path="/local-rank/*" element={<div className="container py-6"><LocalRankSection /></div>} />
                      {/* keyword-research routes removed — tools now live on /keywords page */}
                      <Route path="/search" element={<div className="container py-6"><SearchPage /></div>} />
                      <Route path="/queue" element={<div className="container py-6"><QueuePage /></div>} />
                      <Route path="/logs" element={<div className="container py-6"><LogsPage /></div>} />
                      <Route path="/prompts" element={<div className="container py-6"><PromptsPage /></div>} />
                      <Route path="/workflows" element={<div className="container py-6"><WorkflowsPage /></div>} />
                      <Route path="/workflows/new" element={<WorkflowEditorPage />} />
                      <Route path="/workflows/:id/edit" element={<WorkflowEditorPage />} />
                      <Route path="/workflows/:workflowId/runs/:runId" element={<WorkflowRunDetailPage />} />
                      <Route path="/generate" element={<Navigate to="/workflows" replace />} />
                      <Route path="/analytics" element={<Navigate to="/settings/analytics" replace />} />
                      <Route path="/settings" element={<SettingsPage />}>
                        <Route index element={<Navigate to="/settings/profile" replace />} />
                        <Route path="license" element={<ClientsTab />} />
                        <Route path="websites" element={<WebsiteConnectionsTab />} />
                        <Route path="profile" element={<ProfileTab />} />
                        <Route path="newsroom" element={<NewsroomTab />} />
                        <Route path="transcriptions" element={<TranscriptionsTab />} />
                        <Route path="similarity" element={<SimilarityTab />} />
                        <Route path="verification" element={<SourceVerificationTab />} />
                        <Route path="protocol" element={<SiteProtocolTab />} />
                        <Route path="competitors" element={<CompetitorsTab />} />
                        <Route path="firm-profile" element={<FirmProfileTab />} />
                        <Route path="integrations" element={<IntegrationsTab />} />
                        <Route path="webhooks" element={<WebhooksTab />} />
                        <Route path="image-generation" element={<ImageGenerationTab />} />
                        <Route path="notifications" element={<NotificationsTab />} />
                        {/* Usage is a tab of Analytics; this page was a copy of it. */}
                        <Route path="usage" element={<Navigate to="/settings/analytics?tab=usage" replace />} />
                        <Route path="analytics" element={<AnalyticsTab />} />
                        <Route path="search-console" element={<SearchConsolePage />} />
                        <Route path="account" element={<AccountTab />} />
                        <Route path="api-tokens" element={<ApiTokensTab />} />
                        <Route path="llms" element={<Navigate to="/settings/api-tokens" replace />} />
                      </Route>
                      <Route path="/profile" element={<Navigate to="/settings" replace />} />
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminOverviewPage />} />
                        {/* HQ: the owner's infrastructure map (owner only, see OWNER_ONLY_PATHS). */}
                        <Route path="hq" element={<AdminHqPage />} />
                        {/* Error tracking: PostHog beside Sentry per app (owner only, see OWNER_ONLY_PATHS). */}
                        <Route path="error-tracking" element={<AdminErrorTrackingPage />} />
                        {/* Secret access: every 1Password read on the agent box (owner only, see OWNER_ONLY_PATHS). */}
                        <Route path="secret-access" element={<AdminSecretAccessPage />} />
                        <Route path="clients" element={<AdminClientsPage />} />
                        <Route path="clients/:id" element={<AdminClientDetailPage />} />
                        <Route path="health" element={<AdminClientHealthPage />} />
                        <Route path="team" element={<AdminTeamPage />} />
                        {/* Roles is a tab of Users; Goals was removed (never used). */}
                        <Route path="roles" element={<Navigate to="/admin/users?tab=roles" replace />} />
                        <Route path="goals" element={<Navigate to="/admin" replace />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        {/* People & Access became the Access tab of Users. */}
                        <Route path="people-access" element={<Navigate to="/admin/users?tab=access" replace />} />
                        <Route path="docs" element={<AdminDocsPage />} />
                        <Route path="licenses" element={<AdminLicensesPage />} />
                        <Route path="sites/:id" element={<AdminSiteDetailPage />} />
                        <Route path="feedback" element={<AdminFeedbackPage />} />
                        <Route path="prompt-log" element={<AdminPromptLogPage />} />
                        <Route path="ai-visibility" element={<AdminAiVisibilityPage />} />
                        <Route path="local-rank" element={<AdminLocalRankPage />} />
                        <Route path="api-activity" element={<AdminApiActivityPage />} />
                        <Route path="webhooks" element={<AdminWebhooksPage />} />
                        {/* Invoices became a tab of Payroll (FOR-311). */}
                        <Route path="invoices" element={<Navigate to="/admin/payroll?tab=invoices" replace />} />
                        <Route path="payroll" element={<AdminPayrollPage />} />
                        <Route path="payroll/people/:personKey" element={<AdminPayrollPersonPage />} />
                        <Route path="compensation" element={<AdminCompensationPage />} />
                        <Route path="compensation/:personKey" element={<AdminCompensationPersonPage />} />
                        <Route path="client-pulse" element={<AdminClientPulsePage />} />
                        <Route path="client-dashboards" element={<AdminClientDashboardsPage />} />
                        <Route path="client-dashboards/:siteId" element={<AdminClientDashboardPreviewPage />} />
                        {/* Server vitals moved to Uptime's Servers tab. */}
                        <Route path="infrastructure" element={<Navigate to="/admin/uptime?tab=servers" replace />} />
                        {/* Usage folded back into the admin home as its Usage card. */}
                        <Route path="usage" element={<Navigate to="/admin" replace />} />
                        <Route path="uptime" element={<AdminUptimePage />} />
                        <Route path="site-email" element={<AdminSiteEmailPage />} />
                        <Route path="uptime/:id" element={<AdminUptimeDetailPage />} />
                        <Route path="settings" element={<AdminPlatformSettingsPage />} />
                      </Route>
                      <Route path="/tasks" element={<div className="container py-6"><TasksPage /></div>} />
                      <Route path="/tasks/settings" element={<div className="container py-6"><TasksSettingsPage /></div>} />
                      <Route path="/tasks/:taskId" element={<div className="container py-6"><TasksPage /></div>} />
                      <Route path="/projects" element={<div className="container py-6"><ProjectsPage /></div>} />
                      <Route path="/projects/:projectId" element={<div className="container py-6"><ProjectDetailPage /></div>} />
                      {/* FOR-326: the Agents workspace, owners only (gated inside and by the API). */}
                      <Route path="/agents/*" element={<div className="container py-6"><AgentsSection /></div>} />
                      {/* Our own products' numbers, owners only (gated inside and by the API). */}
                      <Route path="/products/*" element={<div className="container py-6"><ProductsSection /></div>} />
                      {/* FOR-350: our products' support inbox, owners only (gated inside and by the API).
                          No container: the inbox fills the content area; its other pages add their own. */}
                      <Route path="/support/*" element={<SupportSection />} />
                      <Route path="/invoices" element={<div className="container py-6"><InvoicesPage /></div>} />
                      <Route path="/training/*" element={<TrainingSection />} />
                      {/* Creator Studio (FOR-286): its own nested routes, see pages/studio. */}
                      <Route path="/studio/*" element={<div className="container py-6"><StudioSection /></div>} />
                      <Route path="/outreach/*" element={<div className="container py-6"><OutreachSection /></div>} />
                      <Route path="/events/*" element={<div className="container py-6"><EventsSection /></div>} />
                      <Route path="*" element={<UnknownRoute />} />
                    </Routes>
                    </StudioOnlyGate>
                    </Suspense>
                    </RouteErrorBoundary>
                    </div>
                  </main>
                </div>
                <Toaster />
              </DocsProvider>
              </PreferredSidebarProvider>
              </WorkspaceProvider>
            </PrivateRoute>
          }
        />
      </Routes>
        </Suspense>
        </RouteErrorBoundary>
    </BrowserRouter>
      </KeyboardShortcutProvider>
    </AuthProvider>
  )
}

export default App
